import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { createContext } from '@allergan-data-labs/alle-elements-core';
import { ActionMenuContext } from './action-menu-context';

export type ActionMenuContextProps = {
  isRootOpen?: boolean;
  closeOnSelect?: boolean;
  rootMenuRef: React.RefObject<HTMLElement>;
  submenuItems: HTMLButtonElement[] & HTMLDivElement[];
  setIsRootOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSubmenuItems: React.Dispatch<
    React.SetStateAction<HTMLButtonElement[] & HTMLDivElement[]>
  >;
};

export const [ActionSubMenuProvider, useActionSubMenuContext] =
  createContext<ActionMenuContextProps>({
    requiresProvider: true,
    name: 'ActionSubMenuContext',
  });

export const useActionSubMenu = () => {
  const { colorMode } = useContext(ActionMenuContext);

  const {
    isOpen: isSubmenuOpen,
    onOpen: onSubmenuOpen,
    onClose: onSubmenuClose,
  } = useDisclosure();

  const submenuRef = useRef<HTMLDivElement>(null);
  const menuTriggerItemRef = useRef<HTMLButtonElement>(null);
  const {
    isRootOpen,
    rootMenuRef,
    submenuItems,
    setSubmenuItems,
    setIsRootOpen,
  } = useActionSubMenuContext();

  const onOpenHandler = useCallback(() => {
    onSubmenuOpen();
    const id = setTimeout(() => {
      submenuRef.current
        ?.getElementsByTagName('button')[0]
        ?.focus({ preventScroll: true });
      clearTimeout(id);
    });
  }, [onSubmenuOpen]);

  const onCloseHandler = useCallback(() => {
    onSubmenuClose();
    const id = setTimeout(() => {
      menuTriggerItemRef?.current?.focus({ preventScroll: true });
      clearTimeout(id);
    });
  }, [onSubmenuClose]);

  const onKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>) => {
      const isSameNode = menuTriggerItemRef.current?.isSameNode(
        event.target as HTMLElement
      );
      const keyMap: Record<string, React.KeyboardEventHandler> = {
        Escape: (e: React.KeyboardEvent) => {
          e.stopPropagation();
          setIsRootOpen(false);
        },
        ArrowLeft: (e: React.KeyboardEvent) => {
          if (!isSameNode) {
            e.stopPropagation();
          }
          onCloseHandler();
        },
        ArrowRight: (e: React.KeyboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (isSameNode) {
            onOpenHandler();
          }
        },
        Enter: (e: React.KeyboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (isSameNode) {
            onOpenHandler();
          }
        },
        Space: (e: React.KeyboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (isSameNode) {
            onOpenHandler();
          }
        },
      };
      event.code in keyMap && keyMap[event.code](event);
    },
    [onCloseHandler, onOpenHandler, setIsRootOpen]
  );

  const onMouseEnter = useCallback(() => {
    onSubmenuOpen();
  }, [onSubmenuOpen]);

  const onMouseLeave = useCallback(() => {
    const activeElementContainsHoveredElement =
      document.activeElement?.contains(menuTriggerItemRef?.current);
    if (activeElementContainsHoveredElement && isSubmenuOpen) {
      const id = setTimeout(() => {
        const isSameNode = document.activeElement?.isSameNode(
          menuTriggerItemRef?.current
        );
        const isSubmenuFocused = submenuRef?.current?.contains(
          document.activeElement
        );
        if (isSameNode || isSubmenuFocused) {
          return;
        }
        onSubmenuClose();
        clearTimeout(id);
      }, 50);
    }
  }, [isSubmenuOpen, onSubmenuClose]);

  useEffect(() => {
    if (!isRootOpen) {
      onSubmenuClose();
    }
  }, [isRootOpen, onSubmenuClose]);

  useOutsideClick({
    ref: rootMenuRef,
    handler: () => {
      const activeElementIsInMenu = submenuItems.some(
        (node: HTMLButtonElement) => {
          const containSubmenu = node.contains(document.activeElement);
          return containSubmenu;
        }
      );
      if (!activeElementIsInMenu) {
        setIsRootOpen(false);
      }
    },
  });

  return {
    colorMode,
    submenuRef,
    isSubmenuOpen,
    menuTriggerItemRef,
    rootMenuRef,
    submenuItems,
    onSubmenuOpen,
    onSubmenuClose,
    onKeyDownHandler,
    onOpenHandler,
    onCloseHandler,
    onMouseEnter,
    onMouseLeave,
    setSubmenuItems,
  };
};
